/**
 * Utility to convert server-side model predictions (from the Python YOLO server)
 * into the DogDetection shape used by the frontend hooks/components.
 *
 * The Python server returns keypoints with numeric indices (0-N).
 * The dog-pose model has 24 keypoints with specific names (see DOG_KEYPOINT_NAMES).
 * This utility maps indices → names and computes facing/headCenter/tailBase
 * using the same logic as the original client-side dogDetector.ts.
 */

import { DOG_KEYPOINT_NAMES, type DogDetection, type DogKeypoint } from './dogDetector';

export interface ServerKeypoint {
  x: number;
  y: number;
  confidence: number;
  index: number;
}

export interface ServerBoundingBox {
  x1: number;
  y1: number;
  x2: number;
  y2: number;
  width: number;
  height: number;
  confidence: number;
}

export interface ServerDetection {
  bbox: ServerBoundingBox;
  class: string;
  classId: number;
  confidence: number;
  keypoints?: ServerKeypoint[];
}

/**
 * Convert a server detection (raw numeric keypoints) into a DogDetection
 * with named keypoints, facing direction, head/tail centers.
 *
 * Returns null if the detection doesn't have enough keypoints for a valid pose.
 */
export function serverDetectionToDogDetection(
  det: ServerDetection,
  minKeypointConfidence: number = 0.05,
): DogDetection | null {
  // Map numeric keypoints to named keypoints
  const keypoints: DogKeypoint[] = (det.keypoints || []).map((kp) => ({
    name: DOG_KEYPOINT_NAMES[kp.index] || `kpt_${kp.index}`,
    x: kp.x,
    y: kp.y,
    confidence: kp.confidence,
  }));

  // Compute facing from specific keypoints
  const facing = calculateFacingFromKeypoints(keypoints, minKeypointConfidence);

  return {
    bbox: {
      x: det.bbox.x1,
      y: det.bbox.y1,
      width: det.bbox.width,
      height: det.bbox.height,
      confidence: det.bbox.confidence,
    },
    keypoints,
    headCenter: facing?.headCenter || null,
    tailBase: facing?.tailBase || null,
    facing: facing
      ? {
          angle: facing.angle,
          angleDegrees: facing.angleDegrees,
          direction: facing.direction,
          confidence: facing.confidence,
        }
      : null,
    isValid: !!facing,
  };
}

/**
 * Calculate facing direction from named keypoints.
 * Same logic as dogDetector.ts calculateFacing(), extracted for reuse.
 */
function calculateFacingFromKeypoints(
  keypoints: DogKeypoint[],
  minConf: number,
):
  | {
      angle: number;
      angleDegrees: number;
      direction: string;
      confidence: number;
      headCenter: { x: number; y: number };
      tailBase: { x: number; y: number };
    }
  | null {
  const leftEarBase = keypoints.find((kp) => kp.name === 'left_ear_base');
  const rightEarBase = keypoints.find((kp) => kp.name === 'right_ear_base');
  const tailStart = keypoints.find((kp) => kp.name === 'tail_start');

  if (
    !leftEarBase ||
    leftEarBase.confidence < minConf ||
    !rightEarBase ||
    rightEarBase.confidence < minConf ||
    !tailStart ||
    tailStart.confidence < minConf
  ) {
    return null;
  }

  // Head center = midpoint between ear bases
  const headCenter = {
    x: (leftEarBase.x + rightEarBase.x) / 2,
    y: (leftEarBase.y + rightEarBase.y) / 2,
  };

  const tailBase = { x: tailStart.x, y: tailStart.y };

  // Vector from tail to head
  const dx = headCenter.x - tailBase.x;
  const dy = headCenter.y - tailBase.y;

  let angle = Math.atan2(dy, dx);
  angle = normalizeAngle(angle);
  const angleDegrees = angle * (180 / Math.PI);
  const direction = angleToDirection(angle);
  const confidence = (leftEarBase.confidence + rightEarBase.confidence + tailStart.confidence) / 3;

  return { angle, angleDegrees, direction, confidence, headCenter, tailBase };
}

function normalizeAngle(angle: number): number {
  while (angle > Math.PI) angle -= 2 * Math.PI;
  while (angle < -Math.PI) angle += 2 * Math.PI;
  return angle;
}

function angleToDirection(angle: number): string {
  const degrees = normalizeAngle(angle) * (180 / Math.PI);
  if (degrees >= -22.5 && degrees < 22.5) return 'right';
  if (degrees >= 22.5 && degrees < 67.5) return 'down-right';
  if (degrees >= 67.5 && degrees < 112.5) return 'down';
  if (degrees >= 112.5 && degrees < 157.5) return 'down-left';
  if (degrees >= 157.5 || degrees < -157.5) return 'left';
  if (degrees >= -157.5 && degrees < -112.5) return 'up-left';
  if (degrees >= -112.5 && degrees < -67.5) return 'up';
  if (degrees >= -67.5 && degrees < -22.5) return 'up-right';
  return 'right';
}